// Copy affordances (DESIGN_SPEC §10.3). Feedback stays at the trigger, in ink (never green).
//   <CopyButton value label className? />   24px icon button (44px on coarse pointers); copies the FULL value; check for 1.2 s; polite "<label> copied"
//   <CopyText value altValue? label? className?>copy all</CopyText>   text button; Shift-click copies altValue (e.g. JSON)
//   Both copy through lib/clipboard's copyText (navigator.clipboard, falling back to execCommand on plain-http LAN hosts).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { copyText } from "@/lib/clipboard";
import { cx } from "./cx";

type Status = "idle" | "copied" | "failed";

function useCopyStatus(): [Status, (s: Status) => void] {
  const [status, setStatus] = useState<Status>("idle");
  const timer = useRef(0);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const set = (s: Status) => {
    window.clearTimeout(timer.current);
    setStatus(s);
    if (s !== "idle") timer.current = window.setTimeout(() => setStatus("idle"), 1200);
  };
  return [status, set];
}

export function CopyButton({ value, label, className }: { value: string; label: string; className?: string }) {
  const [status, setStatus] = useCopyStatus();
  return (
    <>
      <button
        type="button"
        className={cx("copy", className)}
        aria-label={`Copy ${label}`}
        title={`Copy ${label}`}
        data-copied={status === "copied" ? "" : undefined}
        onClick={async () => setStatus((await copyText(value)) ? "copied" : "failed")}
      >
        <svg className="cp" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
          <rect x="5.75" y="5.75" width="8.5" height="8.5" rx="1" />
          <path d="M10.25 5.75V2.75a1 1 0 0 0-1-1h-6.5a1 1 0 0 0-1 1v6.5a1 1 0 0 0 1 1h3" />
        </svg>
        <svg className="ok" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 8.5l3.2 3L13 4.5" />
        </svg>
      </button>
      <span className="sr-only" role="status">
        {status === "copied" ? `${label} copied` : status === "failed" ? `Couldn't copy ${label}` : ""}
      </span>
    </>
  );
}

export function CopyText({
  value,
  altValue,
  label = "values",
  className,
  children = "copy all",
}: {
  value: string;
  altValue?: string;
  label?: string;
  className?: string;
  children?: ReactNode;
}) {
  const [status, setStatus] = useCopyStatus();
  return (
    <>
      <button
        type="button"
        className={cx("copy-text", className)}
        title={altValue ? "Shift-click copies JSON" : undefined}
        onClick={async (e) => setStatus((await copyText(e.shiftKey && altValue ? altValue : value)) ? "copied" : "failed")}
      >
        {status === "copied" ? "copied" : status === "failed" ? "copy failed" : children}
      </button>
      <span className="sr-only" role="status">
        {status === "copied" ? `${label} copied` : ""}
      </span>
    </>
  );
}
